import { useEffect, useRef, useCallback } from 'react';
import { PresenceWebSocket, apiClient } from '../api/client';

const WS_REFRESH_INTERVAL = 2000;   // Ask for WS updates every 2 seconds
const HTTP_POLL_INTERVAL = 3000;    // HTTP fallback poll every 3 seconds

export function useTeacherPresence(classId, onPresenceUpdate, onNewAttempt) {
  const wsRef = useRef(null);
  const wsIntervalRef = useRef(null);
  const httpIntervalRef = useRef(null);
  const lastUpdateRef = useRef(0);

  // HTTP polling fallback - always runs to ensure we get updates
  const pollPresence = useCallback(async () => {
    if (!classId) return;

    try {
      const data = await apiClient.get(`/classes/${classId}/presence`);
      if (data.students) {
        console.log('[Teacher HTTP] Got', data.students.length, 'students');
        onPresenceUpdate({
          type: 'presence_update',
          classId: classId,
          students: data.students
        });
        lastUpdateRef.current = Date.now();
      }
    } catch (err) {
      console.error('[Teacher HTTP] Poll failed:', err);
    }
  }, [classId, onPresenceUpdate]);

  useEffect(() => {
    if (!classId) {
      console.log('[Teacher] No classId, skipping');
      return;
    }

    console.log('[Teacher] Connecting to class:', classId);

    // Create WebSocket
    wsRef.current = new PresenceWebSocket(classId, (data) => {
      if (data.type === 'presence_update') {
        console.log('[Teacher WS] Got students:', data.students?.length);
        onPresenceUpdate(data);
        lastUpdateRef.current = Date.now();
      } else if (data.type === 'new_attempt' && onNewAttempt) {
        console.log('[Teacher WS] New attempt:', data.attempt);
        onNewAttempt(data.attempt);
      }
    });
    wsRef.current.connect();

    // Ask WS for updates regularly
    wsIntervalRef.current = setInterval(() => {
      if (wsRef.current) {
        wsRef.current.send({ type: 'request_update' });
      }
    }, WS_REFRESH_INTERVAL);

    // HTTP polling fallback - always poll to ensure we get updates
    // This is more reliable than WebSocket for some network conditions
    httpIntervalRef.current = setInterval(pollPresence, HTTP_POLL_INTERVAL);

    // Also do an initial HTTP poll
    pollPresence();

    // Cleanup
    return () => {
      console.log('[Teacher] Disconnecting from class:', classId);
      if (wsIntervalRef.current) {
        clearInterval(wsIntervalRef.current);
      }
      if (httpIntervalRef.current) {
        clearInterval(httpIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.disconnect();
      }
    };
  }, [classId, onPresenceUpdate, onNewAttempt, pollPresence]);

  return {};
}
